import type { LucideIcon } from "lucide-react";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Brain,
  CalendarClock,
  DollarSign,
  GitBranch,
  Inbox,
  LayoutDashboard,
  MessageSquare,
  Network,
  Satellite,
  Send,
  Settings,
  ShieldCheck,
  Users,
  Wrench,
} from "lucide-react";

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  badge?: string;
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

export const ROUTE_DESCRIPTIONS: Record<string, string> = {
  "/": "Today's Hub overview with KPIs, live job strip, and activity feed",
  "/today": "Today's Hub overview with KPIs, live job strip, and activity feed",
  "/inbox": "Unified active work across tasks, incidents, and workflows",
  "/inbox/actions": "Items requiring human attention",
  "/inbox/incidents": "Incident triage - stats, MTTR, ack/resolve, linked tasks",
  "/actions": "Legacy action queue redirect to Inbox actions",
  "/work-queue": "Legacy work queue redirect to Inbox",
  "/queue": "Legacy queue redirect to Inbox",
  "/dispatch": "Dispatch work to agent teams",
  "/dispatch/history": "Dispatch history with persona attribution",
  "/dispatch/jarvis": "Legacy dispatch history redirect",
  "/dispatch/jobs": "Dispatched job tracking and retry management",
  "/dispatch/chat": "Chat with agents directly",
  "/chat": "Legacy agent chat redirect to Dispatch",
  "/jobs": "Legacy jobs redirect to dispatch jobs",
  "/analytics": "Historical trends across goals, incidents, deployments, and task throughput",
  "/incidents": "Incident triage - stats, MTTR, ack/resolve, linked tasks",
  "/costs": "Token usage costs and spend analytics",
  "/fleet": "Live agent fleet health and probe status",
  "/fleet/diagnostics": "Fleet diagnostics, probe results, and queue depths",
  "/fleet/:nodeId": "Fleet node detail drawer",
  "/fleet/probes": "Probe status and remediation history",
  "/agent-fleet": "Legacy agent fleet redirect to Fleet",
  "/satellites": "Legacy satellites route for fleet health",
  "/sentinel": "Probe status and remediation history",
  "/probes": "Legacy probe status route",
  "/fleet/signals": "Fleet signals - persona heartbeats, pipeline, classifier histogram",
  "/fleet/schedules": "Fleet schedule registry, agent events, and stale/missed issues",
  "/fleet/reporting": "Evidence-backed operational matrix for all fleet coordinators and sub-agents",
  "/hermes": "Legacy fleet signals redirect",
  "/workflows": "Workflow library and control-plane definitions",
  "/workflows/:workflowId": "Workflow detail drawer",
  "/workflows/runs": "Paginated DAG workflow execution history",
  "/workflows/runs/:runId": "Workflow run details",
  "/scheduler": "Scheduled job management and manual triggers",
  "/cron": "Legacy scheduler redirect",
  "/tools": "Tool catalog with health metrics",
  "/tools/:toolName": "Tool detail drawer",
  "/memory": "Search and explore agent memory",
  "/graph": "Knowledge graph health and metrics",
  "/graph/projects/:projectId": "Knowledge graph project drill-down",
  "/personas": "Agent roster and persona management",
  "/personas/:personaId": "Persona detail drawer",
  "/governance": "Governance controls for app access and MCP administration",
  "/settings": "User preferences, appearance, and notification controls",
  "/settings/notifications": "Notification preferences, digests, and mute windows",
  "/governance/app-access": "Legacy app access redirect to Governance",
  "/governance/mcp/users": "MCP user access, tiers, roles, and enablement",
  "/governance/mcp/scopes": "MCP tier defaults and per-user scope overrides",
  "/governance/mcp/activity": "Recent MCP connections and request activity",
};

export const NAV_SECTIONS: NavSection[] = [
  {
    label: "Inbox",
    items: [
      { to: "/", label: "Today", icon: LayoutDashboard },
      { to: "/inbox", label: "Inbox", icon: Inbox },
      { to: "/inbox/incidents", label: "Incidents", icon: AlertTriangle },
      { to: "/dispatch", label: "Dispatch", icon: Send },
      { to: "/dispatch/chat", label: "Chat", icon: MessageSquare },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/workflows", label: "Workflows", icon: GitBranch },
      { to: "/scheduler", label: "Scheduler", icon: CalendarClock },
      { to: "/analytics", label: "Analytics", icon: BarChart3 },
      { to: "/costs", label: "Costs", icon: DollarSign },
      { to: "/fleet", label: "Agent Fleet", icon: Satellite },
      { to: "/fleet/diagnostics", label: "Diagnostics", icon: Activity },
      { to: "/fleet/schedules", label: "Schedules", icon: CalendarClock },
      { to: "/fleet/reporting", label: "Fleet Reporting", icon: BarChart3 },
    ],
  },
  {
    label: "Control Plane",
    items: [
      { to: "/tools", label: "Tools", icon: Wrench },
      { to: "/memory", label: "Memory", icon: Brain },
      { to: "/graph", label: "Graph", icon: Network },
      { to: "/personas", label: "Personas", icon: Users },
      { to: "/governance", label: "Governance", icon: ShieldCheck },
      { to: "/settings", label: "Settings", icon: Settings },
    ],
  },
];
